import { computed, ref } from 'vue';
import { cloneDeep } from 'lodash';
import type { AclByWorkspace, User, UserWorkspace } from '@/services/models';
import { saveUserDataToLocalStorage } from '@/services/localStorage-users-data';

export const createAuthModule = () => {
  const authToken = ref<string | null>(window.localStorage.getItem('auth-token'));
  const user = ref<User | null>(null);
  const isProfileLoaded = ref(false);
  const isAuthorized = ref(true);
  const acl = ref<AclByWorkspace>({});
  const member = ref<unknown>(null);

  const impersonationRaw = JSON.parse(window.sessionStorage.getItem('impersonation') || 'null');
  const impersonation = ref(impersonationRaw);

  const isAuthenticated = computed(() => !!authToken.value);

  const allWorkspacesObject = computed((): Record<string, UserWorkspace> =>
    (user.value?.workspaces ?? []).reduce<Record<string, UserWorkspace>>((accumulator, item) => {
      accumulator[item.id] = item;
      return accumulator;
    }, {}),
  );

  const allPrivateWorkspaces = computed(() =>
    (user.value?.workspaces ?? []).filter((workspace: UserWorkspace) => workspace.type === 'private'),
  );

  const allPublicWorkspacesIds = computed(() =>
    (user.value?.workspaces ?? [])
      .filter((workspace: UserWorkspace) => workspace.type === 'public')
      .map((workspace: UserWorkspace) => workspace.id),
  );

  const reset = () => {
    user.value = {} as User;
    acl.value = {};
    member.value = null;
    isProfileLoaded.value = false;
    isAuthorized.value = false;
    authToken.value = null;
    impersonation.value = { id: null, email: null };
  };

  const applyLogin = (loggedInUser: User) => {
    user.value = loggedInUser;
    isAuthorized.value = true;
    authToken.value = window.localStorage.getItem('auth-token');
  };

  const applyAuthToken = (token: string | null) => {
    authToken.value = token;
  };

  const mergeUser = (updates: Partial<User> | Record<string, unknown>) => {
    if (!user.value) return;
    const updatedUser = Object.assign(cloneDeep(user.value), updates) as User;
    user.value = updatedUser;
    if (updates.selectedWorkspaces) {
      saveUserDataToLocalStorage(updatedUser.id, {
        selectedWorkspaces: updatedUser.selectedWorkspaces,
      });
    }
  };

  return {
    state: {
      authToken,
      user,
      isProfileLoaded,
      isAuthorized,
      acl,
      member,
      impersonation,
      isAuthenticated,
      allWorkspacesObject,
      allPrivateWorkspaces,
      allPublicWorkspacesIds,
      mergeUser,
      applyLogin,
      applyAuthToken,
    },
    reset,
  };
};
